/**
 * Ask AI's knowledge: the navigator user guide (apps/docs) and the in-app help, cut into
 * short excerpts, and a small keyword search over them (BM25). Pure: the build script reads
 * the files, the API holds the result (apps/api/src/help/knowledge.json).
 */

export interface KnowledgeChunk {
  id: string;
  /** "Building the panel and releasing · Releasing", or "In-app help · Build the panel · Releasing". */
  title: string;
  /** User guide page address, or null for in-app help. */
  url: string | null;
  text: string;
}

export const DOCS_SITE = 'https://docs.cancerexpert.now';

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

/** Plain text from a Starlight MDX page: no front matter, imports, components or markup. */
export function mdxToText(src: string): { title: string; body: string } {
  const fm = /^---\n([\s\S]*?)\n---\n/.exec(src.replace(/\r\n/g, '\n'));
  const title = fm ? (/^title:\s*"?(.*?)"?\s*$/m.exec(fm[1]!)?.[1] ?? '') : '';
  const body = (fm ? src.replace(/\r\n/g, '\n').slice(fm[0].length) : src.replace(/\r\n/g, '\n'))
    .replace(/^import .*$/gm, '')
    // Link and info cards keep their words.
    .replace(
      /<(?:LinkCard|Card)\s+[^>]*?title="([^"]*)"[^>]*?(?:description="([^"]*)")?[^>]*?\/?>/g,
      (_m, t, d) => (d ? `${t}: ${d}` : t),
    )
    // Screens keep their alt text and caption as a description of what is shown.
    .replace(
      /<Screen\s+[^>]*?alt="([^"]*)"[^>]*?(?:caption="([^"]*)")?[^>]*?\/>/g,
      (_m, alt, cap) => `(Screen: ${alt}${cap ? `. ${cap}` : ''})`,
    )
    .replace(/<span class="phase">(P\d)<\/span>/g, '($1, later phase)')
    .replace(/<\/?[A-Za-z][^>]*>/g, '')
    .replace(/^:::\w+(?:\[(.*?)\])?\s*$/gm, (_m, t) => (t ? `${t}:` : ''))
    .replace(/^:::\s*$/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/^\|\s*-[-\s|]*\|\s*$/gm, '')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { title, body };
}

/** A guide page cut at its "##" headings; each piece keeps the page title for context. */
export function guideChunks(slug: string, src: string): KnowledgeChunk[] {
  const { title, body } = mdxToText(src);
  const page = slug ? `${DOCS_SITE}/${slug}/` : `${DOCS_SITE}/`;
  const parts = body.split(/^## /m);
  const out: KnowledgeChunk[] = [];
  const intro = parts[0]!.trim();
  if (intro) out.push({ id: `guide:${slug || 'index'}`, title, url: page, text: intro });
  for (const part of parts.slice(1)) {
    const nl = part.indexOf('\n');
    const heading = (nl === -1 ? part : part.slice(0, nl)).trim();
    const text = (nl === -1 ? '' : part.slice(nl + 1)).trim();
    if (!text) continue;
    out.push({
      id: `guide:${slug || 'index'}#${slugify(heading)}`,
      title: `${title} · ${heading}`,
      url: `${page}#${slugify(heading)}`,
      text,
    });
  }
  return out;
}

/** One in-app help topic as excerpts: its summary, and one per section. */
export function helpChunks(
  id: string,
  topic: {
    title: string;
    summary: string;
    sections: { heading: string; points: string[] }[];
    later?: { phase: string; text: string }[];
  },
): KnowledgeChunk[] {
  const out: KnowledgeChunk[] = [
    {
      id: `help:${id}`,
      title: `In-app help · ${topic.title}`,
      url: null,
      text: [topic.summary, ...(topic.later ?? []).map((l) => `Later phase (${l.phase}): ${l.text}`)].join(
        '\n',
      ),
    },
  ];
  for (const sec of topic.sections) {
    out.push({
      id: `help:${id}#${slugify(sec.heading)}`,
      title: `In-app help · ${topic.title} · ${sec.heading}`,
      url: null,
      text: sec.points.map((p) => `- ${p}`).join('\n'),
    });
  }
  return out;
}

/**
 * Every excerpt Ask AI may draw on: the user guide's pages (slug, MDX source) and the
 * in-app help topics. Sorted by id so the built file is stable.
 */
export function collectKnowledge(
  pages: { slug: string; source: string }[],
  help: Record<string, Parameters<typeof helpChunks>[1]>,
): KnowledgeChunk[] {
  const all = [
    ...pages.flatMap((p) => guideChunks(p.slug, p.source)),
    ...Object.entries(help)
      .filter(([id]) => id !== 'general')
      .flatMap(([id, t]) => helpChunks(id, t)),
  ];
  return all.sort((a, b) => a.id.localeCompare(b.id));
}

/** A guide file path under content/docs to its page slug: `cases/panel.mdx` → `cases/panel`, `index.mdx` → ``. */
export function guideSlug(relPath: string): string {
  const p = relPath.replace(/\\/g, '/').replace(/\.mdx?$/, '');
  return p === 'index' ? '' : p.replace(/\/index$/, '');
}

/* ---------- search ---------- */

const STOP = new Set(
  'a an the and or but if then else of to in on at by for with from as is are was were be been being do does did how what when where which who whom why can could should would will i me my we our you your it its this that these those there here not no yes so than too very just about into over under again any all each some such only own same both more most other also get got make made use using used want need please tell show give know let'.split(
    ' ',
  ),
);

function stem(w: string): string {
  if (w.length > 5 && w.endsWith('ing')) return w.slice(0, -3);
  if (w.length > 4 && w.endsWith('ed')) return w.slice(0, -2);
  if (w.length > 4 && w.endsWith('es')) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
}

export function terms(text: string): string[] {
  return (text.toLowerCase().match(/[a-z0-9]+/g) ?? []).filter((w) => w.length > 1 && !STOP.has(w)).map(stem);
}

export interface SearchIndex {
  chunks: KnowledgeChunk[];
  docTerms: Map<string, number>[];
  df: Map<string, number>;
  avgLen: number;
}

export function buildIndex(chunks: KnowledgeChunk[]): SearchIndex {
  const docTerms = chunks.map((c) => {
    const m = new Map<string, number>();
    // The title counts twice: it names what the excerpt is about.
    for (const t of [...terms(c.title), ...terms(c.title), ...terms(c.text)]) m.set(t, (m.get(t) ?? 0) + 1);
    return m;
  });
  const df = new Map<string, number>();
  for (const m of docTerms) for (const t of m.keys()) df.set(t, (df.get(t) ?? 0) + 1);
  const avgLen = docTerms.reduce((n, m) => n + [...m.values()].reduce((a, b) => a + b, 0), 0) / chunks.length;
  return { chunks, docTerms, df, avgLen };
}

export interface Hit {
  chunk: KnowledgeChunk;
  score: number;
}

/** BM25 over the excerpts; `prefer` ids (the screen the person is on) get a small lift. */
export function search(index: SearchIndex, query: string, limit = 6, prefer: string[] = []): Hit[] {
  const q = [...new Set(terms(query))];
  if (!q.length) return [];
  const n = index.chunks.length;
  const k1 = 1.2;
  const b = 0.75;
  const hits: Hit[] = [];
  index.chunks.forEach((chunk, i) => {
    const tf = index.docTerms.at(i)!;
    const len = [...tf.values()].reduce((a, c) => a + c, 0);
    let score = 0;
    for (const t of q) {
      const f = tf.get(t);
      if (!f) continue;
      const d = index.df.get(t) ?? 0;
      const idf = Math.log(1 + (n - d + 0.5) / (d + 0.5));
      score += idf * ((f * (k1 + 1)) / (f + k1 * (1 - b + (b * len) / index.avgLen)));
    }
    if (score > 0 && prefer.some((p) => chunk.id === p || chunk.id.startsWith(`${p}#`))) score *= 1.25;
    if (score > 0) hits.push({ chunk, score });
  });
  return hits.sort((x, y) => y.score - x.score).slice(0, limit);
}

/** How many of the question's words the knowledge knows at all: a scope check before any model call. */
export function knownTermShare(index: SearchIndex, query: string): number {
  const q = [...new Set(terms(query))];
  if (!q.length) return 0;
  return q.filter((t) => index.df.has(t)).length / q.length;
}
